import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface StudentSummary {
  relationshipId: string
  startedAt: string
  studentId: string
  fullName: string | null
  email: string
  phone: string | null
  g1LicenceNumber: string | null
  g1IssueDate: string | null
  g1ExpiryDate: string | null
  externalSchoolId: string | null
  externalSchoolName: string | null
  schoolId: string | null
  schoolName: string | null
  packageId: string | null
  packageName: string | null
}

export interface EndedStudentSummary extends StudentSummary {
  endedAt: string
}

interface UseStudentsReturn {
  students: StudentSummary[]
  loading: boolean
  error: string | null
  settingSchoolFor: string | null
  setExternalSchool: (relationshipId: string, externalSchoolId: string | null) => Promise<{ ok: boolean; code?: string }>
  endingRelationshipFor: string | null
  endRelationship: (relationshipId: string) => Promise<{ ok: boolean; code?: string }>
  refresh: () => void
  // Ended (former) students — fetched lazily via fetchEndedStudents(), never automatically,
  // so pages that only need the active list (Schools.tsx's per-school student view, this
  // hook's other callers) never pay for the extra queries.
  endedStudents: EndedStudentSummary[]
  loadingEnded: boolean
  errorEnded: string | null
  fetchEndedStudents: () => void
}

export function useStudents(): UseStudentsReturn {
  const { user } = useAuth()
  const [students, setStudents] = useState<StudentSummary[]>([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState<string | null>(null)
  const [settingSchoolFor, setSettingSchoolFor] = useState<string | null>(null)
  const [endingRelationshipFor, setEndingRelationshipFor] = useState<string | null>(null)
  const [endedStudents, setEndedStudents] = useState<EndedStudentSummary[]>([])
  const [loadingEnded, setLoadingEnded] = useState(false)
  const [errorEnded, setErrorEnded] = useState<string | null>(null)

  const fetchStudents = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)

    try {
      // Step 1: Fetch active instructor-student relationships.
      // RLS policy "relationships: own read" already restricts rows to those where
      // instructor_id = auth.uid() OR student_id = auth.uid(). Since an instructor
      // is never in the student_id column, all returned rows belong to this instructor.
      // external_school_id + instructor_external_schools(name): the instructor's own
      // private attribution label (migration 055) — never an official DriveTrack
      // tenant. RLS on instructor_external_schools already restricts the embedded join
      // to rows this caller owns, same as every other field here.
      // school_id + schools(name): the REGISTERED school this student is attributed to, if
      // any (mutually exclusive with external_school_id — chk_relationships_school_or_external
      // enforces only one is ever set). "schools: member read" RLS (migration 008) allows the
      // join whenever this instructor has an active school_memberships row for that school,
      // which Enroll.tsx's own form already guarantees is true for every school_id it ever
      // writes here (its dropdown only ever offers schools from useInstructorSchoolMemberships).
      const { data: rels, error: relErr } = await supabase
        .from('instructor_student_relationships')
        .select('id, started_at, student_id, external_school_id, instructor_external_schools(name), school_id, schools(name), package_id, school_packages(name)')
        .eq('status', 'active')
        .order('started_at', { ascending: true })

      if (relErr) { setError('Could not load students.'); return }
      if (!rels || rels.length === 0) { setStudents([]); return }

      const studentIds = rels.map(r => r.student_id)

      // Step 2: Fetch public profiles for those students.
      // RLS policy "profiles: instructor read students" allows this for active relationships.
      const { data: profiles, error: profErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, phone')
        .in('id', studentIds)

      if (profErr) { setError('Could not load student profiles.'); return }

      // Step 3: Fetch student-specific profile data.
      // RLS policy "student_profiles: instructor read" allows this for active relationships.
      const { data: studentProfiles, error: spErr } = await supabase
        .from('student_profiles')
        .select('user_id, g1_licence_number, g1_issue_date, g1_expiry_date')
        .in('user_id', studentIds)

      if (spErr) { setError('Could not load student details.'); return }

      // Merge the three result sets by student_id
      const profileMap   = new Map((profiles ?? []).map(p => [p.id, p]))
      const spMap        = new Map((studentProfiles ?? []).map(sp => [sp.user_id, sp]))

      const merged: StudentSummary[] = rels.map((rel: any) => {
        const profile = profileMap.get(rel.student_id)
        const sp      = spMap.get(rel.student_id)
        return {
          relationshipId: rel.id,
          startedAt:      rel.started_at,
          studentId:      rel.student_id,
          fullName:       profile?.full_name ?? null,
          email:          profile?.email     ?? '',
          phone:          profile?.phone     ?? null,
          g1LicenceNumber: sp?.g1_licence_number ?? null,
          g1IssueDate:     sp?.g1_issue_date     ?? null,
          g1ExpiryDate:    sp?.g1_expiry_date    ?? null,
          externalSchoolId:   rel.external_school_id ?? null,
          externalSchoolName: rel.instructor_external_schools?.name ?? null,
          schoolId:   rel.school_id ?? null,
          schoolName: rel.schools?.name ?? null,
          packageId:   rel.package_id ?? null,
          packageName: rel.school_packages?.name ?? null,
        }
      })

      setStudents(merged)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchStudents() }, [fetchStudents])

  const setExternalSchool = useCallback(async (relationshipId: string, externalSchoolId: string | null) => {
    setSettingSchoolFor(relationshipId)
    try {
      // Authorization (ownership of both the relationship and the target external
      // school) is entirely re-verified server-side inside this SECURITY DEFINER
      // function (migration 055) — relationshipId/externalSchoolId are never trusted
      // as proof of anything on their own.
      // p_external_school_id is uuid NOT NULL in Postgres terms (no DEFAULT), but NULL is
      // an intentional, documented value this function accepts to clear attribution
      // (migration 055) — the generated RPC Args type doesn't capture that, hence the cast.
      const { data, error: rpcError } = await supabase.rpc('set_student_external_school', {
        p_relationship_id: relationshipId,
        p_external_school_id: externalSchoolId,
      } as { p_relationship_id: string; p_external_school_id: string })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string } | null
      if (result?.ok) await fetchStudents()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setSettingSchoolFor(null)
    }
  }, [fetchStudents])

  const fetchEndedStudents = useCallback(async () => {
    if (!user) return
    setLoadingEnded(true)
    setErrorEnded(null)

    try {
      // Former students only. Reads rely on the SAME RLS policies as the active list above,
      // widened (migration 060) to also allow status = 'ended' — a concrete, documented gap
      // found while building this view: without that widening, "profiles: instructor read
      // students" and "student_profiles: instructor read" revoke access entirely the moment
      // a relationship ends, not merely hide it in the UI.
      const { data: rels, error: relErr } = await supabase
        .from('instructor_student_relationships')
        .select('id, started_at, ended_at, student_id, external_school_id, instructor_external_schools(name), school_id, schools(name), package_id, school_packages(name)')
        .eq('status', 'ended')
        .order('ended_at', { ascending: false })

      if (relErr) { setErrorEnded('Could not load former students.'); return }
      if (!rels || rels.length === 0) { setEndedStudents([]); return }

      const studentIds = rels.map(r => r.student_id)

      const { data: profiles, error: profErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, phone')
        .in('id', studentIds)
      if (profErr) { setErrorEnded('Could not load former student profiles.'); return }

      const { data: studentProfiles, error: spErr } = await supabase
        .from('student_profiles')
        .select('user_id, g1_licence_number, g1_issue_date, g1_expiry_date')
        .in('user_id', studentIds)
      if (spErr) { setErrorEnded('Could not load former student details.'); return }

      const profileMap = new Map((profiles ?? []).map(p => [p.id, p]))
      const spMap       = new Map((studentProfiles ?? []).map(sp => [sp.user_id, sp]))

      const merged: EndedStudentSummary[] = rels.map((rel: any) => {
        const profile = profileMap.get(rel.student_id)
        const sp      = spMap.get(rel.student_id)
        return {
          relationshipId: rel.id,
          startedAt:      rel.started_at,
          endedAt:        rel.ended_at,
          studentId:      rel.student_id,
          fullName:       profile?.full_name ?? null,
          email:          profile?.email     ?? '',
          phone:          profile?.phone     ?? null,
          g1LicenceNumber: sp?.g1_licence_number ?? null,
          g1IssueDate:     sp?.g1_issue_date     ?? null,
          g1ExpiryDate:    sp?.g1_expiry_date    ?? null,
          externalSchoolId:   rel.external_school_id ?? null,
          externalSchoolName: rel.instructor_external_schools?.name ?? null,
          schoolId:   rel.school_id ?? null,
          schoolName: rel.schools?.name ?? null,
          packageId:   rel.package_id ?? null,
          packageName: rel.school_packages?.name ?? null,
        }
      })

      setEndedStudents(merged)
    } finally {
      setLoadingEnded(false)
    }
  }, [user])

  const endRelationship = useCallback(async (relationshipId: string) => {
    setEndingRelationshipFor(relationshipId)
    try {
      // Ownership and current-status re-verification happen entirely server-side inside
      // this SECURITY DEFINER function (migration 059) — relationshipId is never trusted
      // as proof of anything on its own.
      const { data, error: rpcError } = await supabase.rpc('end_student_relationship', {
        p_relationship_id: relationshipId,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string } | null
      if (result?.ok) await fetchStudents()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setEndingRelationshipFor(null)
    }
  }, [fetchStudents])

  return {
    students, loading, error,
    settingSchoolFor, setExternalSchool,
    endingRelationshipFor, endRelationship,
    refresh: fetchStudents,
    endedStudents, loadingEnded, errorEnded, fetchEndedStudents,
  }
}
